import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ShoppingBag, Download, Fuel } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { useAuth, useCart } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchCart } = useCart();
  
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });

        setPaymentDetails(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
          await fetchCart(); // Refresh cart after successful payment
        } else if (response.data.status === "expired") {
          setStatus("failed");
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts, navigate, fetchCart]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-neon-cyan animate-spin mx-auto mb-6" />
          <h1 className="font-heading text-2xl uppercase mb-4">Processing Payment</h1>
          <p className="text-zinc-400">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
        <div className="text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="font-heading text-2xl uppercase mb-4">Payment Failed</h1>
          <p className="text-zinc-400 mb-8">
            We couldn't verify your payment. If you believe this is an error, please contact support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cart">
              <Button className="bg-neon-cyan text-black">
                Return to Cart
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-white/20">
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="text-center max-w-lg">
        <div className="relative mb-8">
          <CheckCircle className="w-20 h-20 text-neon-green mx-auto animate-pulse-glow" />
          <div className="absolute inset-0 bg-neon-green/20 blur-3xl rounded-full" />
        </div>
        
        <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-4">
          Payment Successful!
        </h1>
        
        <p className="text-zinc-400 mb-8">
          Thank you for your purchase! Your order has been confirmed and you'll receive a confirmation email shortly.
        </p>

        {paymentDetails && (
          <div className="glass p-6 mb-8 text-left">
            <h2 className="font-heading text-lg uppercase tracking-wide mb-4 text-center">Order Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-400">Order Total</span>
                <span className="font-mono text-neon-green font-bold">
                  ${(paymentDetails.amount_total / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Payment Status</span>
                <span className="text-neon-green uppercase text-sm">
                  {paymentDetails.payment_status}
                </span>
              </div>
              {paymentDetails.octane_earned && (
                <>
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 flex items-center gap-1.5">
                      <Fuel className="w-4 h-4 text-neon-yellow" />
                      Octane Earned
                    </span>
                    <span className="font-mono text-neon-yellow font-bold">
                      +{paymentDetails.octane_earned.last_earned} OCT
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Octane Balance</span>
                    <span className="font-mono text-neon-yellow text-sm">
                      {paymentDetails.octane_earned.current_balance?.toLocaleString()} OCT
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm text-zinc-500">
            For beat purchases, download links will be sent to your email.
            For merch orders, you'll receive shipping updates shortly.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/beats">
              <Button className="bg-neon-cyan text-black font-bold uppercase">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Continue Shopping
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-white/20">
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
